import { useParams } from 'react-router';
import { useMutation, useQuery } from 'react-query';
import { useEffect, useState } from 'react';
import { Button, Col, Form, FormGroup, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import TierService from 'api/TierService';
import Spinner from 'components/Spinner/Spinner';
import UploadImage from 'components/UploadImage/UploadImage';
import { GroupFormCheck, Input, TextArea } from 'components/Forms';
import Contents from 'components/Layout/Contents';
import TemplateService from 'api/TemplateService';
import Alert from 'components/Alert/Alert';

export default function EditTier() {
  const { tierId } = useParams();

  const templateQuery = useQuery(
    'template-options',
    TemplateService.getAllTemplatesOptions
  );

  const contentBlockQuery = useQuery(
    'contentBlock-options',
    TemplateService.getAllContentBlocksOptions
  );

  const allowedTemplates = document.querySelectorAll(
    '[name="allowedTemplates"]'
  );
  const allowedBlocks = document.querySelectorAll('[name="allowedBlocks"]');
  const [iconSrc, setIconSrc] = useState('');

  const mutation = useMutation((newTier) =>
    TierService.update(tierId, newTier)
  );

  const { handleSubmit, control, setValue, register } = useForm({
    defaultValues: {
      name: '',
      description: '',
      title: '',
      allowedTemplates: [],
      allowedBlocks: [],
      file: '',
      icon: '',
    },
  });
  const [templateError, setTemplateError] = useState('');
  const [contentBlockError, setContentBlockError] = useState('');
  const { isLoading, refetch } = useQuery(
    ['tiers', tierId],
    () => TierService.getById(tierId),
    {
      enabled: false,
      onSuccess: (data) => {
        setValue('id', data.data?.id);
        setValue('name', data.data?.name);
        setValue('title', data.data?.title);
        setValue('description', data.data?.description);
        setValue('icon', data.data?.icon ? data.data?.icon : '');
        setIconSrc(data.data?.icon ? data.data?.icon : '');
        if (
          allowedTemplates &&
          data.data.allowedTemplates &&
          data.data.allowedTemplates.length > 0
        ) {
          setTimeout(() => {
            data.data.allowedTemplates.map((template) => {
              const obj = document.querySelector(
                `[name="allowedTemplates"][value="${template}"]`
              );
              if (obj) {
                obj.checked = true;
              }
              return template;
            });
            setValue('allowedTemplates', data.data.allowedTemplates);
          }, 100);
        }
        if (allowedBlocks && data.data.allowedBlocks.length > 0) {
          setTimeout(() => {
            data.data.allowedBlocks.map((template) => {
              const obj = document.querySelector(
                `[name="allowedBlocks"][value="${template}"]`
              );
              if (obj) {
                obj.checked = true;
              }
              return template;
            });
            setValue('allowedBlocks', data.data.allowedBlocks);
          }, 1);
        }
      },
    }
  );
  const onSubmit = (formData) => {
    setTemplateError('');
    setContentBlockError('');
    if (formData.allowedTemplates.length === 0) {
      setTemplateError('Please select at least one template.');
      return;
    }
    if (formData.allowedBlocks.length === 0) {
      setContentBlockError('Please select at least one allowed content block.');
      return;
    }
    const payload = { ...formData };
    payload.icon = iconSrc;
    mutation.mutate(payload);
  };

  useEffect(() => {
    // only get the data all other queries are done fetching
    if (tierId && contentBlockQuery.isFetched && templateQuery.isFetched) {
      refetch();
    }
  }, [tierId, contentBlockQuery.isFetched, templateQuery.isFetched]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Contents title="Edit Tier">
      <Form onSubmit={handleSubmit(onSubmit)} id="frmTier">
        {mutation.isLoading && (
          <Alert
            variant="info"
            title="Processing"
            iconProps={{ spin: true, pulse: true }}
          >
            Updating tier...
          </Alert>
        )}
        {mutation.isError && (
          <Alert variant="danger" title="Error">
            An error occurred: {mutation.error.message}
          </Alert>
        )}
        {mutation.isSuccess && (
          <Alert variant="success" title="Processing">
            Tier details updated.
          </Alert>
        )}
        {templateError && templateError.length > 0 && (
          <p className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationCircle} /> {templateError}
          </p>
        )}
        {contentBlockError && contentBlockError.length > 0 && (
          <p className="alert alert-danger">
            <FontAwesomeIcon icon={faExclamationCircle} /> {contentBlockError}
          </p>
        )}
        <input type="hidden" name="id" {...register('id')} />
        <Row className="mb-sm-3">
          <Col sm={12} xl={8} md={8}>
            <FormGroup>
              <Input
                label="Name"
                control={control}
                name="name"
                rules={{ required: true }}
                disabled={mutation.isLoading}
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Title"
                control={control}
                rules={{ required: true }}
                name="title"
                disabled={mutation.isLoading}
              />
            </FormGroup>
            <FormGroup>
              <TextArea
                label="Description"
                control={control}
                rules={{ required: true }}
                name="description"
              />
            </FormGroup>
            <FormGroup>
              <GroupFormCheck
                label="Templates"
                name="allowedTemplates"
                control={control}
                items={templateQuery.data}
                register={register}
                disabled={mutation.isLoading || templateQuery.isFetching}
              />
            </FormGroup>
            <FormGroup>
              <GroupFormCheck
                label="Allowed Content Blocks"
                name="allowedBlocks"
                control={control}
                register={register}
                items={contentBlockQuery.data}
                disabled={mutation.isLoading || contentBlockQuery.isFetching}
              />
            </FormGroup>
          </Col>
          <Col sm={12} xl={4} md={4}>
            <div className="d-flex justify-content-lg-end justify-content-md-end justify-content-sm-center justify-content-center mt-sm-3">
              <UploadImage
                name="file"
                title="Icon"
                srcName="icon"
                register={register}
                src={iconSrc}
                setIconSrc={setIconSrc}
              />
            </div>
          </Col>
        </Row>
        <FormGroup>
          <Button
            variant="primary"
            className="btn-pmi"
            type="submit"
            disabled={mutation.isLoading}
          >
            Save
          </Button>
          <Link as={Button} to="/tiers" className="d-inline-block ms-3">
            Cancel
          </Link>
        </FormGroup>
      </Form>
    </Contents>
  );
}
